import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [data, setData] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email || !data.message) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, data);
      toast.success("Message envoyé. Nous revenons vers vous rapidement.");
      setData({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-pad bg-card/30 border-t border-border">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-6">Contact</div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-[1]">
            Parlons de<br />
            <span className="italic text-[hsl(var(--accent))]">votre projet</span>.
          </h2>
          <p className="mt-8 text-muted-foreground leading-relaxed max-w-md">
            Une question, un site à visiter, un planning à mettre en place ? Notre équipe vous répond.
          </p>

          <div className="mt-12 space-y-6">
            <ContactItem icon={Phone} label="Téléphone" value="0470 62 80 55" href="tel:0470628055" testid="contact-phone" />
            <ContactItem icon={Mail} label="Email" value="info@akaynet.com" href="mailto:info@akaynet.com" testid="contact-email" />
            <ContactItem icon={MapPin} label="Zones" value="Province de Liège & Limbourg, Belgique" testid="contact-zones" />
            <ContactItem icon={Clock} label="Horaires" value="Lun – Sam · 7h – 20h" testid="contact-hours" />
          </div>
        </div>

        <form
          onSubmit={submit}
          className="lg:col-span-7 border border-border rounded-sm p-8 lg:p-10 bg-background space-y-5"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Nom *</Label>
              <Input
                value={data.name}
                onChange={(e) => setData({ ...data, name: e.target.value })}
                placeholder="Votre nom"
                data-testid="contact-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email *</Label>
              <Input
                type="email"
                value={data.email}
                onChange={(e) => setData({ ...data, email: e.target.value })}
                placeholder="vous@email.com"
                data-testid="contact-input-email"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Téléphone</Label>
            <Input
              value={data.phone}
              onChange={(e) => setData({ ...data, phone: e.target.value })}
              placeholder="+32…"
              data-testid="contact-input-phone"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Message *</Label>
            <Textarea
              value={data.message}
              onChange={(e) => setData({ ...data, message: e.target.value })}
              placeholder="Comment pouvons-nous vous aider ?"
              rows={5}
              data-testid="contact-message"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full sm:w-auto disabled:opacity-60"
            data-testid="contact-submit"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Envoi…" : "Envoyer le message"}
          </button>
        </form>
      </div>
    </section>
  );
}

function ContactItem({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center group-hover:border-[hsl(var(--accent))] transition-colors">
        <Icon className="w-4 h-4 text-[hsl(var(--accent))]" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">{label}</div>
        <div className="text-foreground group-hover:text-[hsl(var(--accent))] transition-colors">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testid}>{inner}</a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}
